<template>
  <div>
    <div v-if="isVisible" class="modal fade in" style="display: block;" role="dialog">
      <div class="modal-dialog" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <button type="button" class="close" @click.stop="closeModal" aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
            <h4 class="modal-title">
              <em class="fa fa-plus-circle text-blue"></em> Add Additional Reinvestment
            </h4>
          </div>

          <div class="modal-body">
            <form @submit.prevent="handleSubmit">
              <div class="form-group">
                <label for="amount">Amount <span class="text-danger">*</span></label>
                <input
                  id="amount"
                  v-model="formData.amount"
                  v-money="money"
                  type="text"
                  class="form-control input-lg"
                  placeholder="Enter amount"
                  required
                />
                <span v-if="errors.amount" class="error-feedback">{{ errors.amount }}</span>
              </div>

              <div class="form-group">
                <label for="noteId">Note ID <span class="text-danger">*</span></label>
                <select
                  id="noteId"
                  v-model="formData.noteId"
                  class="form-control input-lg"
                  required
                >
                  <option value="" disabled>Select a note</option>
                  <option
                    v-for="(noteOption, index) in availableNoteIds"
                    :key="index"
                    :value="noteOption.noteId"
                  >
                    {{ noteOption.label }}
                  </option>
                </select>
                <span v-if="errors.noteId" class="error-feedback">{{ errors.noteId }}</span>
                <span v-if="availableNoteIds.length === 0" class="help-block text-warning">
                  <em class="fa fa-info-circle"></em> No notes available. Please ensure you have transactions with action taken that will reinvest.
                </span>
              </div>

              <div class="form-group">
                <label for="fundingSourceType">Funding Source Type <span class="text-danger">*</span></label>
                <select
                  id="fundingSourceType"
                  v-model="selectedFundingSourceIndex"
                  class="form-control input-lg"
                  required
                >
                  <option value="" disabled>Select funding source type</option>
                  <option
                    v-for="(fundingSource, index) in fundingSources"
                    :key="index"
                    :value="index"
                  >
                    {{ fundingSource.name }}
                  </option>
                </select>
                <span v-if="errors.fundingSourceType" class="error-feedback">{{ errors.fundingSourceType }}</span>
              </div>
            </form>
          </div>

          <div class="modal-footer">
            <button type="button" class="btn btn-default" @click.stop="closeModal">Cancel</button>
            <button
              type="button"
              class="btn btn-default"
              :disabled="!isFormValid || submitting"
              @click="handleSubmit"
            >
              {{ submitting ? 'Submitting...' : 'Submit' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="isVisible" class="modal-backdrop fade in"></div>
  </div>
</template>

<script>
import { VMoney } from 'v-money';

export default {
  name: 'AddAdditionalReinvestment',
  props: {
    isVisible: {
      type: Boolean,
      default: false,
    },
    availableNoteIds: {
      type: Array,
      default: () => [],
    },
    fundingSources: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      formData: {
        amount: '',
        noteId: '',
      },
      selectedFundingSourceIndex: '',
      errors: {
        amount: '',
        noteId: '',
        fundingSourceType: '',
      },
      submitting: false,
      money: {
        decimal: '.',
        thousands: ',',
        prefix: '$',
        suffix: '',
        precision: 2,
        masked: false,
      },
    };
  },
  computed: {
    selectedFundingSource() {
      if (this.selectedFundingSourceIndex === '') return null;
      return this.fundingSources[this.selectedFundingSourceIndex];
    },
    isFormValid() {
      return (
        this.formData.amount &&
        this.formData.noteId &&
        this.selectedFundingSource &&
        this.parseAmount(this.formData.amount) > 0 &&
        this.availableNoteIds.length > 0
      );
    },
  },
  watch: {
    isVisible(newVal) {
      if (newVal) {
        this.resetForm();
      }
    },
  },
  methods: {
    closeModal() {
      this.$emit('close');
    },
    parseAmount(amountString) {
      const cleanAmount = amountString.replace(/[^0-9.]/g, '');
      return parseFloat(cleanAmount) || 0;
    },
    validateForm() {
      let isValid = true;
      this.errors = {
        amount: '',
        noteId: '',
        fundingSourceType: '',
      };

      const amount = this.parseAmount(this.formData.amount);
      if (!this.formData.amount || amount <= 0) {
        this.errors.amount = 'Please enter a valid amount greater than $0';
        isValid = false;
      }

      if (!this.formData.noteId) {
        this.errors.noteId = 'Please select a note';
        isValid = false;
      }

      if (!this.selectedFundingSource) {
        this.errors.fundingSourceType = 'Please select a funding source type';
        isValid = false;
      }

      return isValid;
    },
    handleSubmit() {
      if (!this.validateForm()) {
        return;
      }

      this.submitting = true;

      const payload = {
        amount: this.parseAmount(this.formData.amount),
        noteId: this.formData.noteId,
        fundingSourceType: this.selectedFundingSource.type,
      };
      if (this.selectedFundingSource.fundingSourceId) {
        payload.fundingSourceId = this.selectedFundingSource.fundingSourceId;
        payload.fundingSourceName = this.selectedFundingSource.name;
      }

      this.$emit('save', payload);

      // Reset submitting state after a short delay
      setTimeout(() => {
        this.submitting = false;
      }, 500);
    },
    resetForm() {
      this.formData = {
        amount: '',
        noteId: '',
      };
      this.selectedFundingSourceIndex = '';
      this.errors = {
        amount: '',
        noteId: '',
        fundingSourceType: '',
      };
      this.submitting = false;
    },
  },
  directives: { money: VMoney },
};
</script>

<style scoped>
.modal {
  overflow-y: auto;
  padding-top: 30px;
  padding-bottom: 30px;
}

.modal-dialog {
  margin-top: 30px;
}

.modal-header {
  border-bottom: 1px solid #e5e5e5;
  padding: 15px 20px;
}

.modal-title {
  font-size: 18px;
  font-weight: 500;
}

.modal-body {
  padding: 20px;
}

.modal-footer {
  border-top: 1px solid #e5e5e5;
  padding: 15px 20px;
  text-align: right;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #333;
}

.text-danger {
  color: #d9534f;
}

.error-feedback {
  display: block;
  margin-top: 5px;
  color: #d9534f;
  font-size: 13px;
}

.help-block {
  display: block;
  margin-top: 5px;
  font-size: 13px;
}

.text-warning {
  color: #f0ad4e;
}

.text-blue {
  color: #23b7e5;
}

.btn {
  margin-left: 10px;
}

input.form-control,
select.form-control {
  font-size: 14px;
  height: 42px;
}
</style>




// WEBPACK FOOTER //
// AddAdditionalReinvestment.vue?cb3c7e22